import { CircleAlert, Info } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { CurrencyValue, DateValue } from '@/components/common'
import type { RequisitionDecisionContext } from '@/features/recruitment'

/**
 * The headcount being asked for, against what the department already has open.
 *
 * <p>Four requisitions raised separately, each defensible on its own, is how a team of eight
 * becomes a team of twelve without anybody deciding to double it. They are listed here for
 * that reason and no other.
 */
export function RequisitionContext({ detail }: { detail: RequisitionDecisionContext }) {
  const { requisition } = detail

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-[var(--color-heading)]">
          {requisition.reference} · {requisition.title}
        </h3>
        <p className="mt-2 text-sm text-[var(--color-muted)]">
          {requisition.headcount} head(s) for {requisition.departmentName ?? 'no department'}
          {requisition.targetStartDate && (
            <> · wanted by <DateValue value={requisition.targetStartDate} /></>
          )}
        </p>
        {requisition.justification && (
          <p className="mt-2 text-sm">{requisition.justification}</p>
        )}
      </Card>

      <Card
        className={`flex items-start gap-2 p-4 ${
          requisition.budgetChecked ? '' : 'border border-[var(--color-warning)]'
        }`}
      >
        {requisition.budgetChecked
          ? <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          : <CircleAlert className="mt-0.5 size-4 shrink-0 text-[var(--color-warning)]"
              aria-hidden="true" />}
        <div className="text-sm">
          <p className="font-medium text-[var(--color-heading)]">
            {requisition.budgetChecked ? 'Headcount budget' : 'No headcount budget was checked'}
          </p>
          <p className="mt-1 text-[var(--color-muted)]">
            {detail.budgetNote ?? 'The budget check recorded nothing.'}
          </p>
          {detail.salaryRangeNote && (
            <p className="mt-1 text-[var(--color-muted)]">{detail.salaryRangeNote}</p>
          )}
        </div>
      </Card>

      <Card className="p-4">
        <h3 className="text-sm font-semibold text-[var(--color-heading)]">
          Already open in this department
        </h3>
        {detail.otherOpenInDepartment.length === 0 ? (
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            Nothing else is submitted or approved here, so this one stands on its own.
          </p>
        ) : (
          <ul className="mt-2 space-y-1 text-sm">
            {detail.otherOpenInDepartment.map((other) => (
              <li key={other.id} className="flex flex-wrap gap-2">
                <span className="font-mono">{other.reference}</span>
                <span>{other.title}</span>
                <span className="text-[var(--color-muted)]">
                  {other.headcount} head(s) · {other.status.toLowerCase()}
                  {other.salaryRangeMax != null && (
                    <> · up to{' '}
                      <CurrencyValue
                        value={other.salaryRangeMax}
                        currency={other.currency ?? undefined}
                      />
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}
